import { z } from 'zod';
import { Permission } from '../permissions.js';

/**
 * Dynamic RBAC (spec: super-admin defines modules + roles; a role grants
 * "view" or "manage" access per module). Modules are metadata groupings of
 * the existing, server-enforced Permission strings - creating a module does
 * not add new enforcement, it curates which of the fixed permissions a role
 * picking that module (at view/manage tier) receives.
 */
export const moduleAccessTier = z.enum(['view', 'manage']);
export type ModuleAccessTier = z.infer<typeof moduleAccessTier>;

const permissionValues = Object.values(Permission) as [Permission, ...Permission[]];
const permissionSchema = z.enum(permissionValues);

export const createModuleSchema = z.object({
  key: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase letters, numbers and underscores only'),
  label: z.string().min(1).max(80).trim(),
  description: z.string().max(300).trim().optional().default(''),
  icon: z.string().max(40).trim().nullable().optional(),
  viewPermissions: z.array(permissionSchema).default([]),
  managePermissions: z.array(permissionSchema).default([]),
  sortOrder: z.number().int().min(0).max(9999).optional().default(0),
});
export type CreateModuleInput = z.infer<typeof createModuleSchema>;

export const updateModuleSchema = createModuleSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateModuleInput = z.infer<typeof updateModuleSchema>;

export const roleModuleAccessSchema = z.object({
  moduleId: z.string().uuid(),
  access: moduleAccessTier,
});

export const createRoleSchema = z.object({
  name: z.string().min(2).max(60).trim(),
  description: z.string().max(300).trim().optional().default(''),
  moduleAccess: z.array(roleModuleAccessSchema).default([]),
});
export type CreateRoleInput = z.infer<typeof createRoleSchema>;

export const updateRoleSchema = z.object({
  name: z.string().min(2).max(60).trim().optional(),
  description: z.string().max(300).trim().optional(),
  moduleAccess: z.array(roleModuleAccessSchema).optional(),
});
export type UpdateRoleInput = z.infer<typeof updateRoleSchema>;

/** Team member (staff) management - super-admin/delegated-user CRUD. */
export const createTeamMemberSchema = z.object({
  email: z.string().email().max(254).toLowerCase().trim(),
  firstName: z.string().min(1).max(80).trim(),
  lastName: z.string().min(1).max(80).trim(),
  roleId: z.string().uuid(),
});
export type CreateTeamMemberInput = z.infer<typeof createTeamMemberSchema>;

export const updateTeamMemberSchema = z.object({
  firstName: z.string().min(1).max(80).trim().optional(),
  lastName: z.string().min(1).max(80).trim().optional(),
  roleId: z.string().uuid().optional(),
  isSuspended: z.boolean().optional(),
});
export type UpdateTeamMemberInput = z.infer<typeof updateTeamMemberSchema>;

/** Admin's own profile - name + self-service password change. */
export const updateAdminProfileSchema = z.object({
  firstName: z.string().min(1).max(80).trim().optional(),
  lastName: z.string().min(1).max(80).trim().optional(),
});
export type UpdateAdminProfileInput = z.infer<typeof updateAdminProfileSchema>;

export const changeOwnPasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: z
    .string()
    .min(10, 'Use at least 10 characters')
    .max(128)
    .refine((v) => /[a-z]/.test(v) && /[A-Z0-9]/.test(v), 'Mix letters and numbers or capitals'),
});
export type ChangeOwnPasswordInput = z.infer<typeof changeOwnPasswordSchema>;
